import {st} from './i18n';
import type { SassmallCartItemAddRequest } from "./api/cart";

function safeStringify(value: any) {
  try {
    return JSON.stringify(value || {});
  }
  catch {
    return "{}";
  }
}

function normalizeText(value: any) {
  return String(value || "").trim();
}

function centsToAmount(value: any) {
  const cents = Number(value);
  return Number.isFinite(cents) ? Number((cents / 100).toFixed(2)) : 0;
}

export function createSassmallProductDraft(input: Record<string, any>): SassmallCartItemAddRequest {
  const platform = String(input.platform || input.origin || "UNKNOWN").toUpperCase();
  const itemId = String(input.itemId || input.offerId || input.id || "");
  const skuId = String(input.skuId || input.itemSkuId || input.specId || "");
  const rawPrice = input.priceSnapshot ?? input.price ?? input.salePrice ?? input.unitPrice ?? 0;
  const priceSnapshot = Number(rawPrice) || 0;
  return {
    rawUrl: String(input.rawUrl || input.url || (process.client ? window.location.href : "")),
    normalizedUrl: String(input.normalizedUrl || input.rawUrl || input.url || ""),
    platform,
    shopId: String(input.shopId || input.sellerId || ""),
    shopName: String(input.shopName || input.shopNick || input.sellerName || ""),
    itemId,
    skuId,
    skuText: String(input.skuText || input.specsInfoText || input.specsInfo || ""),
    quantity: Math.max(1, Number(input.quantity) || 1),
    title: String(input.title || input.subject || input.itemTitle || ""),
    imageUrl: String(input.imageUrl || input.picUrl || input.pic || input.image || ""),
    priceSnapshot,
    currency: String(input.currency || "CNY"),
    purchaseModeHint: String(input.purchaseModeHint || (input.supportApiPurchase === false ? "MANUAL" : "API")),
    supportApiPurchase: input.supportApiPurchase !== false,
    userRemark: String(input.userRemark || input.remark || ""),
    categoryId: input.categoryId,
    categoryName: String(input.categoryName || ""),
    snapshotJson: input.snapshotJson,
  };
}

export function createSassmallTaobaoDraft(
  detail: Record<string, any>,
  selected: Record<string, any>,
): SassmallCartItemAddRequest {
  const itemId = normalizeText(selected.itemId || detail.itemId);
  const rawUrl = normalizeText(selected.rawUrl) || `https://item.taobao.com/item.htm?id=${itemId}`;
  const priceSnapshot = centsToAmount(selected.priceSnapshot);
  return createSassmallProductDraft({
    rawUrl,
    normalizedUrl: rawUrl,
    platform: "TAOBAO",
    shopId: detail.shopId,
    shopName: detail.shopName,
    itemId,
    skuId: selected.skuId,
    skuText: selected.skuText,
    quantity: selected.quantity,
    title: selected.title || detail.title,
    imageUrl: selected.imageUrl || detail.picUrls?.[0] || detail.pictUrl,
    priceSnapshot,
    categoryId: detail.categoryId ?? detail.category_id,
    categoryName: detail.categoryName || detail.category_name,
    snapshotJson: safeStringify({
      source: "pc_tb_goods_detail",
      detail,
      selected: { ...selected, priceSnapshot },
    }),
  });
}

export function createSassmallWeidianDraft(
  detail: Record<string, any>,
  selected: Record<string, any>,
): SassmallCartItemAddRequest {
  const itemId = normalizeText(selected.itemId || detail.itemId || detail.itemid);
  const skuId = normalizeText(selected.skuId);
  const rawUrl = normalizeText(selected.rawUrl || detail.h5Url)
    || `https://weidian.com/item.html?itemID=${itemId}`;
  const priceSnapshot = centsToAmount(selected.priceSnapshot);
  return createSassmallProductDraft({
    rawUrl,
    normalizedUrl: rawUrl,
    platform: "WEIDIAN",
    shopId: detail.shopId || detail.shop_id || detail.sellerId || detail.seller_id,
    shopName: detail.shopName || detail.shop_name,
    itemId,
    skuId,
    // 微店确实存在没有 SKU/规格维度的商品。保留空的第三方 SKU ID，
    // 同时提供一个逻辑规格文本，兼容购物车快照的完整性校验。
    skuText: normalizeText(selected.skuText) || (!skuId ? st("默认规格") : ""),
    quantity: selected.quantity,
    title: selected.title || detail.title || detail.itemName || detail.item_name,
    imageUrl: selected.imageUrl || detail.picUrls?.[0] || detail.imgs?.[0],
    priceSnapshot,
    categoryId: detail.categoryId ?? detail.category_id,
    categoryName: detail.categoryName || detail.category_name,
    snapshotJson: safeStringify({
      source: "pc_weidian_goods_detail",
      detail,
      selected: { ...selected, priceSnapshot },
    }),
  });
}

export function createSassmallAlibabaDraft(
  detail: Record<string, any>,
  selected: Record<string, any>,
): SassmallCartItemAddRequest {
  const itemId = normalizeText(selected.itemId || selected.offerId || detail.offerId);
  const rawUrl = `https://detail.1688.com/offer/${itemId}.html`;
  return createSassmallProductDraft({
    rawUrl,
    normalizedUrl: rawUrl,
    platform: "ALI_1688",
    shopId: selected.shopId || detail.sellerOpenId,
    shopName: selected.shopName || detail.sellerLoginId,
    itemId,
    skuId: selected.skuId || selected.specId,
    skuText: selected.skuText,
    quantity: selected.quantity,
    title: selected.title || detail.subject,
    imageUrl: selected.imageUrl || detail.productImage?.images?.[0],
    priceSnapshot: selected.priceSnapshot,
    categoryId: selected.categoryId || detail.categoryId,
    categoryName: selected.categoryName || detail.categoryName,
    snapshotJson: safeStringify({ source: "pc_alibaba_goods_detail", detail, selected }),
  });
}
